import { Stack, Typography } from '@mui/material';
import { type ComponentType, useState } from 'react';
import type { ImportRowsProps } from 'src/components/ImportProposalsModal/import-rows';
import type { ImportListRow } from 'src/components/ImportProposalsModal/use-import-list-rows';
import { ModalStep } from 'src/components/ModalContainer';

const formatSelected = (count: number) =>
  count ? `${count} ${count === 1 ? 'project' : 'projects'} selected` : 'No projects selected';

interface ImportSelectStepProps {
  title: string;
  Rows: ComponentType<ImportRowsProps>;
  onReview: (selected: ImportListRow[]) => void;
  reviewLoading?: boolean;
  onCancel: () => void;
  /** Prefix of the step's test ids: `-select`, `-select-cancel`, `-review`. */
  testIdPrefix: string;
}

/** The first step of a project import: pick the projects, then review them. */
export const ImportSelectStep = ({
  title,
  Rows,
  onReview,
  reviewLoading = false,
  onCancel,
  testIdPrefix,
}: ImportSelectStepProps) => {
  const [selected, setSelected] = useState<ImportListRow[]>([]);
  const count = selected.length;

  return (
    <ModalStep
      title={title}
      actions={[
        {
          label: 'Cancel',
          kind: 'secondary',
          onClick: onCancel,
          testId: `${testIdPrefix}-select-cancel`,
        },
        {
          label: `Review ${count} ${count === 1 ? 'item' : 'items'}`,
          kind: 'primary',
          onClick: () => onReview(selected),
          disabled: count === 0,
          loading: reviewLoading,
          testId: `${testIdPrefix}-review`,
        },
      ]}
    >
      <Stack gap={1.5} data-testid={`${testIdPrefix}-select`}>
        <Typography variant="body2" color="text.secondary">
          {formatSelected(count)}
        </Typography>
        <Rows selected={selected} onSelectedChange={setSelected} withSelectAll />
      </Stack>
    </ModalStep>
  );
};
